import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API_ENDPOINTS } from "../../config/apiConfig";
import { useAuth } from "../router/authContext";

const ChevronDownIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
    <path d="m6 9 6 6 6-6" />
  </svg>
);

const MenuIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
    <line x1="4" x2="20" y1="12" y2="12" />
    <line x1="4" x2="20" y1="6" y2="6" />
    <line x1="4" x2="20" y1="18" y2="18" />
  </svg>
);

const CloseIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
    <path d="M18 6 6 18" />
    <path d="m6 6 12 12" />
  </svg>
);

export const LandingNav = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userData, setUserData] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const userMenuTimeoutRef = React.useRef(null);

  const navigate = useNavigate();
  const { logout } = useAuth();

  useEffect(() => {
    axios
      .get(API_ENDPOINTS.user, {
        headers: { Authorization: `Token ${localStorage.getItem("authToken")}` },
      })
      .then((res) => {
        setUserData(res.data);
        setIsAuthenticated(true);
        setLoadingUser(false);
      })
      .catch(() => {
        setIsAuthenticated(false);
        setLoadingUser(false);
      });
  }, []);

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/login");
    setIsUserMenuOpen(false);
    setIsMobileMenuOpen(false);
  };

  const openUserMenu = () => {
    if (userMenuTimeoutRef.current) clearTimeout(userMenuTimeoutRef.current);
    setIsUserMenuOpen(true);
  };
  const closeUserMenu = () => {
    userMenuTimeoutRef.current = setTimeout(() => setIsUserMenuOpen(false), 200);
  };

  React.useEffect(() => () => {
    if (userMenuTimeoutRef.current) clearTimeout(userMenuTimeoutRef.current);
  }, []);
  const navLinkClass = "px-4 py-2 text-sm font-medium text-slate-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors";
  const mobileLinkClass = "block text-slate-700 hover:text-blue-700 hover:bg-blue-50 rounded-lg px-3 py-2.5 font-medium text-sm";

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled
        ? "bg-white/95 backdrop-blur-md shadow-sm border-b border-slate-200/70"
        : "bg-white/80 backdrop-blur-sm border-b border-slate-100/70"
    }`}>
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-[4.5rem]">
          <Link to={isAuthenticated ? "/home" : "/"} className="flex shrink-0 items-center">
            <span className="flex items-center">
              <img
                src="/img/logo.png"
                alt="AmpTrans logo"
                className="h-11 w-auto max-w-none shrink-0 object-contain md:h-12"
              />
            </span>
          </Link>

          <div className="hidden lg:flex items-center gap-1">
            <Link to="/#news" className={navLinkClass}>News</Link>
            <Link to="/#solutions" className={navLinkClass}>Solutions</Link>
            <Link to="/contact" className={navLinkClass}>Contact</Link>
          </div>

          <div className="hidden md:flex items-center gap-3">
            {loadingUser ? (
              <div className="w-6 h-6 border-2 border-slate-300 border-t-blue-600 rounded-full animate-spin" />
            ) : isAuthenticated ? (
              <div className="relative" onMouseEnter={openUserMenu} onMouseLeave={closeUserMenu}>
                <button className="flex items-center gap-1 text-slate-600 hover:text-slate-900 hover:bg-slate-100 px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                  {userData?.first_name || "User"}
                  <ChevronDownIcon />
                </button>
                {isUserMenuOpen && (
                  <div className="absolute right-0 mt-1 w-40 py-1 bg-white/95 backdrop-blur border border-slate-200 rounded-xl shadow-xl">
                    <Link to="/profile" className="block px-4 py-2 text-sm text-slate-700 hover:text-slate-900 hover:bg-slate-100 rounded-lg" onClick={() => { setIsUserMenuOpen(false); }}>
                      Profile
                    </Link>
                    <button onClick={handleLogout} className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-slate-100 rounded-lg">
                      Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Link to="/login" className="inline-flex items-center justify-center h-9 px-4 py-2 rounded-md text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-colors">
                  Login
                </Link>
                <Link
                  to="/subscription"
                  className="inline-flex items-center justify-center h-9 px-5 py-2 text-sm font-medium text-white rounded-lg bg-blue-700 hover:bg-blue-800 shadow transition-all"
                >
                  Get Started
                </Link>
              </>
            )}
          </div>

          <button
            type="button"
            className="lg:hidden text-slate-700 p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="lg:hidden border-t border-slate-200 bg-white px-6 py-4">
          <div className="pt-3 space-y-2">
            <Link to="/#news" className={mobileLinkClass} onClick={() => setIsMobileMenuOpen(false)}>News</Link>
            <Link to="/#solutions" className={mobileLinkClass} onClick={() => setIsMobileMenuOpen(false)}>Solutions</Link>
            <Link to="/contact" className={mobileLinkClass} onClick={() => setIsMobileMenuOpen(false)}>Contact</Link>
            {isAuthenticated ? (
              <>
                <Link to="/profile" className="block text-slate-700 hover:text-blue-700 hover:bg-blue-50 rounded-lg px-3 py-2.5 font-medium text-sm" onClick={() => setIsMobileMenuOpen(false)}>Profile</Link>
                <button type="button" onClick={handleLogout} className="w-full text-left text-red-500 hover:bg-red-50 rounded-lg px-3 py-2.5 font-medium text-sm">Logout</button>
              </>
            ) : (
              <>
                <Link to="/login" className="inline-flex justify-center h-10 items-center rounded-lg border border-slate-300 text-slate-700 font-medium text-sm w-full" onClick={() => setIsMobileMenuOpen(false)}>Login</Link>
                <Link to="/subscription" className="inline-flex justify-center h-10 items-center rounded-lg bg-blue-700 text-white font-medium text-sm w-full" onClick={() => setIsMobileMenuOpen(false)}>Get Started</Link>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};
